import { useState, useEffect, useCallback, useMemo } from "react";
import type { ChangeEvent } from "react";
import { API_BASE_URL } from "../config";
import { getAuthHeaders } from "../api/auth";
import "./AddBatchTransportView.css";

interface TimeWindow {
    _id: string;
    timeWindowFrom: string;
    timeWindowTo: string;
    capacity: number;
    usedCapacity: number;
    availableCapacity: number;
    day?: string; // MO, DI, MI, DO, FR, SA
    timeSlot?: string; // z.B. "06-08"
}

interface BatchTransportAllocation {
    timeWindowId: string;
    quantityTrucks: number;
    timeWindowFrom?: string;
    timeWindowTo?: string;
    day?: string;
    timeSlot?: string;
}

interface AddBatchTransportViewProps {
    transportNumber: string;
    fractionBez: string;
    calWeek: string;
    plant: string;
    existingBatchNumbers: string[];
    maxQuantity?: number; // Maximale Anzahl LKWs (ungeplante Menge)
    onClose: () => void;
    onSave: (allocations: BatchTransportAllocation[]) => Promise<void>;
    onSaveReady?: (saveHandler: () => Promise<void>, canSave: boolean) => void;
    onOverageChange?: (overageCount: number) => void; // Anzahl der zu viel eingeplanten LKWs
    carrierMode?: boolean; // Wenn true, verwendet Carrier-Access-APIs
    getCarrierAuthHeaders?: () => Record<string, string>; // Für Carrier-Mode
}

export function AddBatchTransportView({
    transportNumber,
    fractionBez,
    calWeek,
    plant,
    existingBatchNumbers,
    maxQuantity,
    onClose,
    onSave,
    onSaveReady,
    onOverageChange,
    carrierMode = false,
    getCarrierAuthHeaders
}: AddBatchTransportViewProps) {
    const [timeWindows, setTimeWindows] = useState<TimeWindow[]>([]);
    const [allocations, setAllocations] = useState<Map<string, number>>(new Map());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedDay, setSelectedDay] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    // Lade verfügbare Zeitfenster
    useEffect(() => {
        const fetchTimeWindows = async () => {
            try {
                setLoading(true);
                setError(null);

                let planningUrl: string;
                let headers: HeadersInit;

                if (carrierMode && getCarrierAuthHeaders) {
                    // Carrier-Access-Modus: Verwende carrier-access API (ohne calendarWeek-Validierung)
                    const params = new URLSearchParams();
                    // Füge calendarWeek nur hinzu, wenn sie gültig ist
                    const calWeekStr = String(calWeek);
                    if (calWeek && calWeekStr.match(/^[1-9]$|^[1-4][0-9]$|^5[0-3]$/)) {
                        params.set('calendarWeek', calWeekStr);
                    }
                    params.set('year', new Date().getFullYear().toString());
                    params.set('plant', plant);
                    params.set('_t', Date.now().toString());

                    planningUrl = `${API_BASE_URL}/api/carrier-access/time-windows?${params.toString()}`;
                    headers = getCarrierAuthHeaders();
                } else {
                    // Tenant-Modus: Verwende normale planning API
                    const params = new URLSearchParams({
                        calendarWeek: calWeek,
                        year: new Date().getFullYear().toString(),
                        plant: plant,
                        _t: Date.now().toString()
                    });
                    planningUrl = `${API_BASE_URL}/api/planning/overview?${params.toString()}`;
                    headers = getAuthHeaders();
                }

                const planningResponse = await fetch(planningUrl, {
                    headers,
                    credentials: "include",
                    cache: "no-cache"
                });

                if (!planningResponse.ok) {
                    throw new Error(`Failed to fetch planning data (${planningResponse.status})`);
                }

                const planningData = await planningResponse.json();

                console.log("Loaded planning data:", planningData);
                console.log("Looking for fraction:", fractionBez);

                // Extrahiere Zeitfenster basierend auf Modus
                const extractedTimeWindows: TimeWindow[] = [];

                // Both carrier and tenant mode now use the same overview structure
                if (planningData.overview) {
                    // Extrahiere aus overview-Struktur
                    // Durchlaufe alle Tage und Zeitslots
                    Object.entries(planningData.overview).forEach(([day, timeSlots]: [string, any]) => {
                        Object.entries(timeSlots).forEach(([timeSlot, data]: [string, any]) => {
                            console.log(`[${day} ${timeSlot}] id:${data.id}, has capacities:${!!data.capacities}, has fraction:${!!(data.capacities && data.capacities[fractionBez])}`);
                            // Zeige alle Zeitfenster mit verfügbarer Kapazität (id kann null sein für neue Planungen)
                            if (data.capacities && data.capacities[fractionBez]) {
                                const capacity = data.capacities[fractionBez];
                                const availableCapacity = capacity.total - capacity.used;

                                if (availableCapacity > 0) {
                                    // Berechne timeWindowFrom und timeWindowTo aus Tag und Zeitslot
                                    const year = planningData.year || new Date().getFullYear();
                                    const week = planningData.calendarWeek || parseInt(calWeek);

                                    // ISO 8601: Erste Woche des Jahres ist die Woche mit dem ersten Donnerstag
                                    // Finde den ersten Donnerstag des Jahres
                                    const jan1 = new Date(year, 0, 1);
                                    const jan1Day = jan1.getDay() || 7; // Sonntag = 7 statt 0

                                    // Tage bis zum ersten Donnerstag
                                    const daysToFirstThursday = (4 - jan1Day + 7) % 7;
                                    const firstThursday = new Date(year, 0, 1 + daysToFirstThursday);

                                    // Montag der ersten ISO-Woche
                                    const firstMonday = new Date(firstThursday);
                                    firstMonday.setDate(firstThursday.getDate() - 3); // Donnerstag - 3 = Montag

                                    // Berechne Montag der Zielwoche
                                    const targetDate = new Date(firstMonday);
                                    targetDate.setDate(firstMonday.getDate() + (week - 1) * 7);

                                    // Addiere Wochentag-Offset (MO=0, DI=1, ..., SA=5)
                                    const dayIndex = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA'].indexOf(day);
                                    if (dayIndex >= 0) {
                                        targetDate.setDate(targetDate.getDate() + dayIndex);
                                    }

                                    // Parse Zeitfenster (z.B. "06-08")
                                    const [startHour, endHour] = timeSlot.split('-').map(h => parseInt(h));

                                    targetDate.setHours(startHour, 0, 0, 0);
                                    const timeWindowFrom = targetDate.toISOString();

                                    const endDate = new Date(targetDate);
                                    endDate.setHours(endHour || startHour + 2, 0, 0, 0);
                                    const timeWindowTo = endDate.toISOString();

                                    // ✅ Verwende die echte ZEITSLOT_ID aus dem Backend (30 chars gepaddet)
                                    // Fallback zu generierter ID nur wenn data.id nicht vorhanden
                                    const timeWindowId = data.id || `${day}-${timeSlot}-${fractionBez}`;

                                    extractedTimeWindows.push({
                                        _id: timeWindowId,
                                        timeWindowFrom,
                                        timeWindowTo,
                                        capacity: capacity.total,
                                        usedCapacity: capacity.used,
                                        availableCapacity,
                                        day,
                                        timeSlot
                                    });
                                }
                            }
                        });
                    });
                }
                console.log(`Extracted ${extractedTimeWindows.length} time windows for ${fractionBez}`);
                // Sortiere nach Wochentag (MO-SA) und dann nach Zeitslot
                const dayOrder = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA'];
                extractedTimeWindows.sort((a, b) => {
                    const dayDiff = dayOrder.indexOf(a.day || '') - dayOrder.indexOf(b.day || '');
                    if (dayDiff !== 0) return dayDiff;

                    // Bei gleichem Tag nach Zeitslot sortieren
                    const aStartHour = parseInt((a.timeSlot || '').split('-')[0]) || 0;
                    const bStartHour = parseInt((b.timeSlot || '').split('-')[0]) || 0;
                    return aStartHour - bStartHour;
                });

                setTimeWindows(extractedTimeWindows);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Fehler beim Laden der Zeitfenster");
            } finally {
                setLoading(false);
            }
        };

        void fetchTimeWindows();
    }, [fractionBez, calWeek, plant, refreshKey]);

    const totalAllocated = Array.from(allocations.values()).reduce((sum, qty) => sum + qty, 0);

    // Expose save handler to parent
    useEffect(() => {
        if (onSaveReady) {
            const exceedsMax = maxQuantity !== undefined && totalAllocated > maxQuantity;
            onSaveReady(handleSave, totalAllocated > 0 && !exceedsMax && !loading && !saving);
        }

        // Informiere Parent über Überschreitung
        if (onOverageChange && maxQuantity !== undefined) {
            const overage = Math.max(0, totalAllocated - maxQuantity);
            onOverageChange(overage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalAllocated, maxQuantity, loading, saving]);

    const handleAllocationChange = useCallback((timeWindowId: string, quantity: number) => {
        setAllocations(prev => {
            const next = new Map(prev);
            if (quantity > 0) {
                next.set(timeWindowId, quantity);
            } else {
                next.delete(timeWindowId);
            }
            return next;
        });
    }, []);

    const handleQuickFill = useCallback((timeWindowId: string) => {
        const timeWindow = timeWindows.find(tw => tw._id === timeWindowId);
        if (!timeWindow) return;

        // Fülle automatisch mit verfügbarer Kapazität
        handleAllocationChange(timeWindowId, timeWindow.availableCapacity);
    }, [timeWindows, handleAllocationChange]);

    const handleSave = async () => {
        if (totalAllocated === 0) {
            alert("Bitte planen Sie mindestens 1 LKW ein");
            return;
        }

        // Validierung: Nicht mehr als ungeplant einplanen
        if (maxQuantity !== undefined && totalAllocated > maxQuantity) {
            alert(`Sie können maximal ${maxQuantity} LKW${maxQuantity === 1 ? '' : 's'} einplanen (ungeplante Menge). Aktuell geplant: ${totalAllocated}`);
            return;
        }

        setSaving(true);
        try {
            const allocationArray: BatchTransportAllocation[] = Array.from(allocations.entries()).map(
                ([timeWindowId, quantityTrucks]) => {
                    // Finde das zugehörige TimeWindow
                    const timeWindow = timeWindows.find(tw => tw._id === timeWindowId);
                    return {
                        timeWindowId,
                        quantityTrucks,
                        // Zeitfenster-Daten direkt mitgeben
                        timeWindowFrom: timeWindow?.timeWindowFrom,
                        timeWindowTo: timeWindow?.timeWindowTo,
                        day: timeWindow?.day,
                        timeSlot: timeWindow?.timeSlot
                    };
                }
            );

            await onSave(allocationArray);

            // Setze Allocations zurück und lade Zeitfenster neu
            setAllocations(new Map());
            setRefreshKey(prev => prev + 1);

            // Optional: Schließe die View nicht automatisch, damit User weitere Chargen einplanen kann
            // onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Fehler beim Speichern");
        } finally {
            setSaving(false);
        }
    };

    const formatTime = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });
    };

    const getCapacityColor = (used: number, total: number) => {
        const percentage = (used / total) * 100;
        if (percentage >= 90) return "#dc2626"; // Rot
        if (percentage >= 70) return "#f59e0b"; // Orange
        return "#16a34a"; // Grün
    };

    // Gruppiere Zeitfenster nach Wochentag
    const timeWindowsByDay = useMemo(() => {
        const days = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA'];
        const grouped: Record<string, TimeWindow[]> = {};

        days.forEach(day => {
            grouped[day] = timeWindows.filter(tw => tw.day === day);
        });

        return grouped;
    }, [timeWindows]);

    // Gefilterte Zeitfenster basierend auf ausgewähltem Tag
    const displayedTimeWindows = useMemo(() => {
        if (!selectedDay) return [];
        return timeWindowsByDay[selectedDay] || [];
    }, [selectedDay, timeWindowsByDay]);

    // Zähle verfügbare Kapazität pro Tag
    const capacityByDay = useMemo(() => {
        const days = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA'];
        const capacity: Record<string, number> = {};

        days.forEach(day => {
            const dayWindows = timeWindowsByDay[day] || [];
            capacity[day] = dayWindows.reduce((sum, tw) => sum + tw.availableCapacity, 0);
        });

        return capacity;
    }, [timeWindowsByDay]);

    // Extrahiere Datum pro Tag
    const dateByDay = useMemo(() => {
        const days = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA'];
        const dates: Record<string, string> = {};

        days.forEach(day => {
            const dayWindows = timeWindowsByDay[day] || [];
            if (dayWindows.length > 0) {
                const date = new Date(dayWindows[0].timeWindowFrom);
                dates[day] = `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.`;
            }
        });

        return dates;
    }, [timeWindowsByDay]);

    return (
        <div className="add-batch-transport-view">
            {loading ? (
                <div className="add-batch-transport-loading">
                    <span className="add-batch-transport-spinner" />
                    <p>Lade Zeitfenster...</p>
                </div>
            ) : error ? (
                <div className="add-batch-transport-error">
                    <p>{error}</p>
                    <button onClick={() => setError(null)}>OK</button>
                </div>
            ) : timeWindows.length === 0 ? (
                <div className="add-batch-transport-empty">
                    <p>Keine Zeitfenster verfügbar für {fractionBez} in KW {calWeek}</p>
                </div>
            ) : (
                <>
                    {/* Tag-Auswahl Buttons */}
                    <div className="day-selector">
                        {['MO', 'DI', 'MI', 'DO', 'FR', 'SA'].map(day => {
                            const dayCapacity = capacityByDay[day] || 0;
                            const hasCapacity = dayCapacity > 0;

                            return (
                                <button
                                    key={day}
                                    type="button"
                                    className={`day-button ${selectedDay === day ? 'day-button--active' : ''} ${!hasCapacity ? 'day-button--disabled' : ''}`}
                                    onClick={() => setSelectedDay(day)}
                                    disabled={!hasCapacity}
                                >
                                    <div className="day-button-label">{day}</div>
                                    {dateByDay[day] && (
                                        <div className="day-button-date">{dateByDay[day]}</div>
                                    )}
                                    <div className="day-button-capacity">
                                        {hasCapacity ? `${dayCapacity} frei` : 'Voll'}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Zeitfenster für ausgewählten Tag */}
                    {selectedDay && (
                        <div className="time-windows-list">
                            <h3 className="time-windows-title">{selectedDay} - Zeitfenster</h3>

                            {displayedTimeWindows.length === 0 ? (
                                <div className="time-windows-empty">
                                    <p>Keine Zeitfenster verfügbar für {selectedDay}</p>
                                </div>
                            ) : (
                                <div className="time-windows-grid">
                                    {displayedTimeWindows.map((tw) => {
                                        const allocated = allocations.get(tw._id) || 0;
                                        const remaining = tw.availableCapacity - allocated;
                                        const usedPercentage = ((tw.usedCapacity + allocated) / tw.capacity) * 100;

                                        return (
                                            <div key={tw._id} className="time-window-card">
                                                <div className="time-window-header">
                                                    <div className="time-window-time">
                                                        {tw.timeSlot} Uhr
                                                    </div>
                                                    <div className="time-window-capacity">
                                                        <span className="time-window-capacity-value">{remaining} / {tw.capacity}</span>
                                                        <span className="time-window-capacity-label">frei</span>
                                                    </div>
                                                </div>

                                                {/* Kapazitätsbalken */}
                                                <div className="time-window-capacity-bar">
                                                    <div
                                                        className="time-window-capacity-bar-fill"
                                                        style={{
                                                            width: `${Math.min(usedPercentage, 100)}%`,
                                                            backgroundColor: getCapacityColor(tw.usedCapacity + allocated, tw.capacity)
                                                        }}
                                                    />
                                                </div>

                                                {/* Eingabe */}
                                                <div className="time-window-input-group">
                                                    <label htmlFor={`allocation-${tw._id}`} className="time-window-input-label">
                                                        LKW-Anzahl:
                                                    </label>
                                                    <div className="time-window-input-controls">
                                                        <input
                                                            id={`allocation-${tw._id}`}
                                                            type="number"
                                                            min="0"
                                                            max={tw.availableCapacity}
                                                            value={allocated}
                                                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                                const value = Math.min(
                                                                    Math.max(0, parseInt(e.target.value) || 0),
                                                                    tw.availableCapacity
                                                                );
                                                                handleAllocationChange(tw._id, value);
                                                            }}
                                                            className="time-window-input"
                                                        />
                                                        <button
                                                            type="button"
                                                            className="time-window-quick-fill-button"
                                                            onClick={() => handleQuickFill(tw._id)}
                                                            disabled={tw.availableCapacity === 0}
                                                            title="Automatisch füllen"
                                                        >
                                                            Max
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Warnung bei Überbuchung */}
                                                {allocated > tw.availableCapacity && (
                                                    <div className="time-window-warning">
                                                        ⚠️ Kapazität überschritten um {allocated - tw.availableCapacity} LKW(s)
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
